import diffInSeconds from 'date-fns/differenceInSeconds';
import { jwtDecode } from 'jwt-decode';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import { assign, fromPromise, setup } from 'xstate';

import { Role, SessionSchema } from '@eluve/api-contract';
import { LogLevel, Logger, NoopLogger } from '@eluve/logger';

import {
  TenantTokenMintForbiddenError,
  TenantTokenMintUnavailableError,
} from './tenant-token-errors';
import { ISessionProvider } from './types';

/**
 * Configuration for retry behavior on token acquisition failures.
 * Uses exponential backoff to avoid hammering the auth service.
 */
const RETRY_CONFIG = {
  /** Maximum number of retry attempts before giving up */
  maxAttempts: 3,
  /** Base delay in milliseconds for exponential backoff */
  baseDelayMs: 500,
  /** Maximum delay cap in milliseconds */
  maxDelayMs: 4000,
} as const;

type Context = {
  jwt: string;
  defaultRole: Role;
  /**
   * If this machine will be used for managing a tenant scoped session,
   * this is the tenant that we ultimately want to have a token for
   */
  desiredTenantId?: string;
  /**
   * The tenant ID that was signed on the current token
   */
  tokenTenantId?: string;
  /**
   * A function that can be used to reset the token eg by starting a new tenant session.
   * Legacy fallback only — the mint path below never touches the shared session. [ENG-5545]
   */
  reset: () => Promise<void>;
  /**
   * Mints a tenant-scoped JWT for this tab WITHOUT mutating the shared
   * SuperTokens session. When supplied (and a desired tenant is set), token
   * acquisition uses this instead of reading the shared session's token, so
   * tabs on different tenants coexist instead of fighting over the single
   * x-hasura-tenant-id claim. [ENG-5545]
   */
  mintTenantToken: ((tenantId: string) => Promise<string>) | undefined;
  attemptingRefreshAfterMs: number;
  sessionProvider: ISessionProvider;
  logger: Logger;
  /**
   * Current number of token acquisition attempts (for retry logic)
   */
  acquisitionAttempts: number;
  /**
   * Current number of tenant-session reset attempts for the active desired tenant.
   */
  resetAttempts: number;
  /**
   * Current number of token refresh attempts (for refresh retry logic).
   */
  refreshAttempts: number;
};

type Input = Pick<Context, 'sessionProvider'> & { logger?: Logger };

type Events =
  | { type: 'CHANGE_TENANT'; desiredTenantId: string }
  | { type: 'SUPPLY_RESET_LOGIC'; reset: () => Promise<void> }
  | {
      type: 'SUPPLY_TENANT_TOKEN_MINTER';
      mintTenantToken: (tenantId: string) => Promise<string>;
    }
  | { type: 'SUPPLY_LOGGER'; logger: Logger }
  | { type: 'ACQUIRE_TOKEN' }
  | { type: 'SESSION_COMPONENT_UNMOUNTED' }
  | { type: 'HARD_REFRESH_REQUESTED' };

export const sessionMachine = setup({
  types: {
    input: {} as Input,
    context: {} as Context,
    events: {} as Events,
  },
  actions: {
    logMessage: (
      { context },
      params: {
        level: LogLevel;
        message: string;
        logMeta?: Record<string, unknown>;
      },
    ) => {
      const { level, message, logMeta = {} } = params;
      context.logger[level](message, logMeta);
    },
  },
  guards: {
    isTokenTenantValid: ({ context }) => {
      if (isNil(context.desiredTenantId)) {
        return true;
      }

      return context.tokenTenantId === context.desiredTenantId;
    },
    shouldRetryAcquisition: ({ context }) => {
      return context.acquisitionAttempts < RETRY_CONFIG.maxAttempts;
    },
    usesMintedTenantToken: ({ context }) => {
      return Boolean(context.desiredTenantId && context.mintTenantToken);
    },
    hasNotAttemptedReset: ({ context }) => {
      return context.resetAttempts === 0;
    },
  },
  actors: {
    resetToken: fromPromise<void, { reset: () => Promise<void> }>(
      async ({ input: { reset } }) => await reset(),
    ),
    getToken: fromPromise<
      {
        jwt: string;
        expiresInSeconds: number;
        defaultRole: Role;
        tenantId: string | undefined;
      },
      {
        sessionProvider: ISessionProvider;
        desiredTenantId?: string;
        mintTenantToken?: (tenantId: string) => Promise<string>;
      }
    >(
      async ({
        input: { sessionProvider, desiredTenantId, mintTenantToken },
      }) => {
        const deriveTokenValues = (jwt: string, payload: unknown) => {
          const result = SessionSchema.parse(payload);
          const hasuraClaims = result['https://hasura.io/jwt/claims'];
          const defaultRole = hasuraClaims['x-hasura-default-role'];

          const accessTokenExpiry = jwtDecode(jwt)?.exp;
          if (!accessTokenExpiry) {
            throw new Error('Access token expiry not found');
          }

          const now = Math.floor(Date.now() / 1000);
          const expiresInSeconds = diffInSeconds(
            new Date(accessTokenExpiry * 1000),
            new Date(now * 1000),
          );

          return {
            expiresInSeconds,
            jwt,
            defaultRole,
            tenantId: hasuraClaims['x-hasura-tenant-id'],
          };
        };

        // Tenant-scoped path: mint a per-tab JWT for the desired tenant without
        // touching the shared session, so tabs on other tenants are unaffected.
        // [ENG-5545]
        if (desiredTenantId && mintTenantToken) {
          try {
            const jwt = await mintTenantToken(desiredTenantId);
            return deriveTokenValues(jwt, jwtDecode(jwt));
          } catch (e) {
            if (!(e instanceof TenantTokenMintUnavailableError)) {
              throw e;
            }
            // Old backend (rollback window): fall through to the legacy
            // shared-session flow below; a tenant mismatch there is handled by
            // the ResettingToken/createTenantSession path as before.
          }
        }

        const jwt = (await sessionProvider.getAccessToken()) ?? '';
        const payload = await sessionProvider.getAccessTokenPayloadSecurely();
        return deriveTokenValues(jwt, payload);
      },
    ),

    refreshToken: fromPromise<boolean, { sessionProvider: ISessionProvider }>(
      async ({ input: { sessionProvider } }) => {
        return sessionProvider.attemptRefreshingSession();
      },
    ),
  },
  delays: {
    preemptivelyRefreshToken: ({ context }) => {
      return context.attemptingRefreshAfterMs;
    },
    acquisitionRetryBackoff: ({ context }) => {
      // Exponential backoff: baseDelay * 2^(attempts-1)
      // e.g., with baseDelay=500: 500ms, 1000ms, 2000ms, ...
      const delay = Math.min(
        RETRY_CONFIG.baseDelayMs * Math.pow(2, context.acquisitionAttempts - 1),
        RETRY_CONFIG.maxDelayMs,
      );
      return delay;
    },
    refreshRetryBackoff: ({ context }) => {
      // Exponential backoff mirroring acquisitionRetryBackoff, keyed on
      // refreshAttempts: 500ms, 1000ms, 2000ms, ... capped at maxDelayMs.
      const delay = Math.min(
        RETRY_CONFIG.baseDelayMs * Math.pow(2, context.refreshAttempts - 1),
        RETRY_CONFIG.maxDelayMs,
      );
      return delay;
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QGU6wJYHsB2ACAsgIYDGAFutmAMQCCAwgIoCqAkgEoCiA+gCoDyAaQ4A5ANoAGALqJQAB0wYALlmwyQAD0QBWAOwAaEAE9EAJgAcAZgB0ATksWAbCfNmtARnE6HAX28HUsBg4BCTklFR0ABI0wgDi3DwiMTwS0kgg8koqapoIugbGCBbFVg72DgAsjjZOFTq+-mgqIWQU1MhMAAqdADIAmlycyBw8XD18sSx0qWqZ6Mo4OYhuFVoVtmZeFiYWZnbiZhUFiDY2blbiJu7ixWXipw0gAUF4RK3hHd39YxPxbDPpOYLVTpXIrHTrCpXNzFHRXG4OY4INwmR7PZpvMLtDjIZAsPjCLh0Pj4ToEkSjJjCfB8KmJAAiALkCnm2VB2n0RmWXlKWnE-NO7hRbh8fieTWCmLaVmEmB4mAA1mBsFQmRkWcClsi9g4rCitCY3DY6lV+YiuUUqlYLDo+dtxDCHJcLGiJa9QtKaMQAI4AV3QACcKFB5UqVRAcGArBQAG6KqPoyUeyhWL1+wPB0PKhCxzDEQjA1JqoFs0Bgs42a38w02G5uLxHC2VdbiPmeCriBxrZyosWJ93vKNp-1B7Ah+MqsABgOYANWWQAGwLADNZwBbKz9lpY1M+keZic57Bx-OFqTFjWljSmCoVXUWPkmCrGnY1CFIhxuLTW1atrt2CxOxdPs3W3aUADVCAXdAIALA8w1VKRZkvRZ2TySoLn5G4nx2EwdEcJFn11Zw+ThLQnQfB96hAwIMWTKNIOg2DlDHLMVVENw0mZLJULLbQMKw7CoW2fDzUKQ5Kx0W0Dg8VtLhMGxXVopNBysNg4DARQWPHBCIxTXMlU3UCpRTdTYE07S2KPE84JwIskMBFCQT47Uaj1K4axNQDOyRHQbGsfVOy-KTxGfMwlJeMDTI0rT4OVKgpxnOdFxXdcjOUgcdzMiy4uway81s7B7K49UeOc69XN1fVPIhbyxMQSxKzMeEHAsVYTAOG0Iro1T1OXAM4DCHT4r0qMDITYz6LUsB+sG3L8tPFRiuQsqtTcHV3INI0vLNJF3ErPDSN2UKu3rbqVKymaBtgIa2IS6dZ3nJdFFXAMNy3Eyoz667bsPXNFrs88HO41leIq9a3Oq7bat2i0O3EC5pOKMwUdapxzsy6U2JoGNCHQJcACMF2odRYEUAso0IZdFCnAAKWQBrANdZGUGMwAXQxvsGtiAEoqA+qbsdx-HCCJsAL1WtC3C-c5PxMT9XA8swu0ImxvzqUj4RR4otGAsVsEwCA4DUAXBxW0HytyGwkT2a0207CFzFWT8MaiqMWAgYnzc1ND5Y63lLnItXDmfLQPyNKx5dImF9ThYDGgyt2ZTlCdvavMF3HWEUyja+XVh0VwkQsCPmsfVwzBhNqtFdz7d3TUdhvKkswdyDrjVsG18M8PCYSqPa7AuX84QcMpXBqGupsYmC4NY1PHMllz8gtAuzFsLCC5WVrTgqCfepiyy55Bn2XJWULNs3xx7g6ixfNcKwyM8fDkdC8KaMi2uuZu3K05b5ZdBMKwbZ1ruBzsaRshRbznBsC1NWWgUY1FCrvHcQs8aEy9vPC2WopKr2gXyXWToV6rEImsWw8IdChX2m4XwvggA */
  id: 'Session Machine',
  context: ({ input: { sessionProvider, logger } }) => ({
    jwt: '',
    defaultRole: 'user',
    attemptingRefreshAfterMs: 0,
    sessionProvider,
    logger:
      logger ?? new Logger('Session Machine', { logger: new NoopLogger() }),
    reset: async () => noop(),
    mintTenantToken: undefined,
    acquisitionAttempts: 0,
    resetAttempts: 0,
    refreshAttempts: 0,
  }),
  on: {
    ACQUIRE_TOKEN: {
      target: '.NoToken',
    },
    CHANGE_TENANT: {
      actions: assign({
        desiredTenantId: ({ event }) => event.desiredTenantId,
        tokenTenantId: undefined,
        jwt: '',
        resetAttempts: 0,
      }),
      target: '.NoToken',
    },
    SUPPLY_RESET_LOGIC: {
      actions: assign({
        reset: ({ event }) => event.reset,
      }),
    },
    SUPPLY_TENANT_TOKEN_MINTER: {
      // Whole-context assigner: a property-level assigner is ambiguous here
      // because the assigned value is itself a 1-arg function, which TS can't
      // distinguish from a PropertyAssigner.
      actions: assign(({ event }) => ({
        mintTenantToken: event.mintTenantToken,
      })),
    },
    SUPPLY_LOGGER: {
      actions: assign({
        logger: ({ event }) => event.logger,
      }),
    },
    SESSION_COMPONENT_UNMOUNTED: {
      actions: assign({
        jwt: '',
        desiredTenantId: undefined,
        tokenTenantId: undefined,
        reset: async () => noop(),
        mintTenantToken: undefined,
        resetAttempts: 0,
      }),
      target: '.Idle',
    },
    HARD_REFRESH_REQUESTED: {
      actions: assign({
        jwt: '',
        resetAttempts: 0,
      }),
      target: '.Idle',
    },
  },
  states: {
    Idle: {
      description:
        'Idle state. Waiting to receive a command to start acquiring a token.',
    },
    NoToken: {
      description: 'No token available',
      always: {
        target: 'AcquiringToken',
        actions: assign({
          acquisitionAttempts: 0,
        }),
      },
    },
    AcquiringToken: {
      description: 'Extracting JWT and core values from session data',
      entry: [
        ({ context }) => {
          const attempt = context.acquisitionAttempts + 1;
          if (attempt > 1) {
            context.logger.info(
              `Acquiring token (attempt ${attempt}/${RETRY_CONFIG.maxAttempts})`,
            );
          }
        },
        assign({
          acquisitionAttempts: ({ context }) => context.acquisitionAttempts + 1,
        }),
      ],
      invoke: {
        src: 'getToken',
        input: ({ context }) => ({
          sessionProvider: context.sessionProvider,
          desiredTenantId: context.desiredTenantId,
          mintTenantToken: context.mintTenantToken,
        }),
        onDone: [
          {
            // The token we just read is already expired (e.g. a stale page
            // load, or a refresh that didn't mint a fresh token). Never serve
            // an expired token — that produces a negative proactive-refresh
            // delay ("Refreshing in -Nms") and, once sent to Hasura, a
            // JWTExpired logout cascade. Refresh it first instead.
            guard: ({ event }) => event.output.expiresInSeconds < 0,
            target: 'RefreshingToken',
            actions: assign({
              // We acquired successfully (the token is just stale), so clear the
              // acquisition retry budget; the refresh path owns recovery now.
              acquisitionAttempts: 0,
            }),
          },
          {
            target: 'ValidatingToken',
            actions: assign({
              defaultRole: ({ event }) => event.output.defaultRole,
              jwt: ({ event }) => event.output.jwt,
              tokenTenantId: ({ event }) => event.output.tenantId,
              attemptingRefreshAfterMs: ({
                event: {
                  output: { expiresInSeconds },
                },
              }) => {
                const val = expiresInSeconds * 0.8 * 1000;
                return val;
              },
              // Reset attempts on success
              acquisitionAttempts: 0,
              refreshAttempts: 0,
            }),
          },
        ],
        onError: [
          {
            // The mint endpoint rejected the tenant server-authoritatively
            // (not a member) — retrying can't help. Same terminal state as a
            // failed legacy tenant switch so TenantGuard's toast/redirect UX
            // applies unchanged. [ENG-5545]
            guard: ({ event }) =>
              event.error instanceof TenantTokenMintForbiddenError,
            target: 'TenantSwitchFailed',
            actions: assign({
              acquisitionAttempts: 0,
            }),
          },
          {
            // If we haven't exhausted retries, go to retry state
            guard: 'shouldRetryAcquisition',
            target: 'RetryingTokenAcquisition',
            actions: ({ context }) => {
              if (context.acquisitionAttempts > 1) {
                context.logger.warn(
                  `Token acquisition failed (attempt ${context.acquisitionAttempts}/${RETRY_CONFIG.maxAttempts}), will retry`,
                );
              }
            },
          },
          {
            // All retries exhausted, give up
            target: 'Idle',
            actions: [
              {
                type: 'logMessage',
                params: {
                  level: 'warn',
                  message: 'Failed to acquire token',
                },
              },
              assign({
                jwt: '',
                tokenTenantId: undefined,
                acquisitionAttempts: 0,
                resetAttempts: 0,
              }),
            ],
          },
        ],
      },
    },
    RetryingTokenAcquisition: {
      description:
        'Waiting with exponential backoff before retrying token acquisition',
      entry: ({ context }) => {
        if (context.acquisitionAttempts > 1) {
          const delay = Math.min(
            RETRY_CONFIG.baseDelayMs *
              Math.pow(2, context.acquisitionAttempts - 1),
            RETRY_CONFIG.maxDelayMs,
          );
          context.logger.info(`Retrying token acquisition in ${delay}ms`);
        }
      },
      after: {
        acquisitionRetryBackoff: 'AcquiringToken',
      },
    },
    ValidatingToken: {
      description:
        'Validating that the token is usable for the given call site. Eg. tenant id matches',
      always: [
        {
          guard: 'isTokenTenantValid',
          target: 'TokenAvailable',
          actions: assign({
            resetAttempts: 0,
          }),
        },
        {
          guard: 'hasNotAttemptedReset',
          target: 'ResettingToken',
        },
        {
          target: 'TenantSwitchFailed',
        },
      ],
    },
    ResettingToken: {
      description:
        'Attempting to reset the token eg. by starting a new tenant session if the tenant id did not match',
      entry: [
        {
          type: 'logMessage',
          params: ({ context }) => ({
            level: 'info',
            message: `Resetting token (attempt ${context.resetAttempts + 1})`,
          }),
        },
        assign({
          resetAttempts: ({ context }) => context.resetAttempts + 1,
        }),
      ],
      invoke: {
        src: 'resetToken',
        input: ({ context }) => ({
          reset: context.reset,
        }),
        onDone: {
          target: 'AcquiringToken',
        },
        onError: {
          actions: {
            type: 'logMessage',
            params: {
              level: 'warn',
              message: 'Failed to reset token',
            },
          },
          target: 'TenantSwitchFailed',
        },
      },
    },
    TenantSwitchFailed: {
      description:
        'Tenant-session reset failed or the refreshed token still does not match the requested tenant',
      entry: [
        {
          type: 'logMessage',
          params: ({ context }) => ({
            level: 'warn',
            message: 'Failed to switch tenant session',
            logMeta: {
              desiredTenantId: context.desiredTenantId,
              tokenTenantId: context.tokenTenantId,
            },
          }),
        },
        assign({
          jwt: '',
          tokenTenantId: undefined,
        }),
      ],
    },
    RefreshingToken: {
      description: 'Attempting to refresh the token',
      entry: [
        {
          type: 'logMessage',
          params: () => ({
            level: 'info',
            message: 'Attempting to refresh token',
          }),
        },
        assign({
          refreshAttempts: ({ context }) => context.refreshAttempts + 1,
        }),
      ],
      invoke: {
        src: 'refreshToken',
        input: ({ context: { sessionProvider } }) => ({ sessionProvider }),
        onDone: [
          {
            // SuperTokens resolves `false` only when the session has genuinely
            // expired (the refresh token itself was rejected). This is the one
            // legitimate logout trigger: clear the token and go Idle so callers
            // get a TokenAcquisitionError and the app re-authenticates.
            guard: ({ event }) => event.output === false,
            target: 'Idle',
            actions: [
              {
                type: 'logMessage',
                params: {
                  level: 'warn',
                  message: 'Session expired; refresh token rejected',
                },
              },
              assign({
                jwt: '',
                tokenTenantId: undefined,
              }),
            ],
          },
          {
            // Refreshed successfully — re-read the freshly minted token.
            target: 'AcquiringToken',
          },
        ],
        // A *thrown* refresh is transient (network blip, refresh timeout): the
        // session is not known to be dead, so we must NOT log the user out.
        // Keep the current token and retry with backoff until the refresh
        // succeeds or the server definitively reports the session expired.
        // Crucially we never transition to Idle here — that dead-end was the
        // cause of the unexpected idle logouts (ENG-4399).
        onError: {
          target: 'RetryingTokenRefresh',
          actions: ({ context }) => {
            if (context.refreshAttempts > 1) {
              context.logger.warn(
                `Token refresh failed (attempt ${context.refreshAttempts}), keeping session and will retry`,
              );
            }
          },
        },
      },
    },
    RetryingTokenRefresh: {
      description:
        'Waiting with exponential backoff before retrying a failed token refresh',
      entry: ({ context }) => {
        if (context.refreshAttempts > 1) {
          const delay = Math.min(
            RETRY_CONFIG.baseDelayMs * Math.pow(2, context.refreshAttempts - 1),
            RETRY_CONFIG.maxDelayMs,
          );
          context.logger.info(`Retrying token refresh in ${delay}ms`);
        }
      },
      after: {
        refreshRetryBackoff: 'RefreshingToken',
      },
    },
    TokenAvailable: {
      description: 'A valid token is available to be used to make requests',
      entry: {
        type: 'logMessage',
        params: ({ context }) => ({
          level: 'info',
          message: `Token acquired. Refreshing in ${context.attemptingRefreshAfterMs}ms`,
        }),
      },
      after: {
        preemptivelyRefreshToken: [
          {
            // Minted per-tab tokens are not refreshable through the shared
            // SuperTokens session — each mint is a fresh token, so just
            // re-acquire. The mint call is itself a cookie-authenticated
            // request, which keeps the underlying session alive. [ENG-5545]
            guard: 'usesMintedTenantToken',
            target: 'AcquiringToken',
          },
          { target: 'RefreshingToken' },
        ],
      },
    },
  },
  initial: 'Idle',
});
